import type { RefObject } from 'react'
import { isActionMenuOpen } from '../lib/openMenus'
import { modalDepth } from '../components/shared/modalStack'
import { useModalLayer } from '../components/shared/useModalLayer'

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
const MODAL_SELECTOR = '[aria-modal="true"]'

/**
 * The overlay keyboard contract ToolSheet established, shared with the
 * search panel, the deck editor and the other sheets and dialogs:
 *  - focus moves into `panelRef` on open and back to the opener on close;
 *  - Escape closes, and Tab cycles inside the panel;
 *  - the shared ref-counted body scroll lock while open.
 *
 * Stacking lives in ONE place (components/shared/modalStack.ts, reached
 * through useModalLayer, shared with ModalShell): only the TOP layer of the
 * whole app gets these keys, a later layer paints above an earlier one, and
 * covered layers are inert and aria-hidden. The covered element is the
 * panel's aria-modal root (the overlay), or the panel itself.
 *
 * `onClose` lives in a ref inside useModalLayer, so the layer registers once
 * per open: re-running it on a parent rerender would re-lock scroll and yank
 * focus mid-use.
 */
export function useModalFocus(
  open: boolean,
  panelRef: RefObject<HTMLElement | null>,
  onClose: () => void,
  opts: {
    initialFocus?: () => HTMLElement | null | undefined
    /** Replaces "focus the opener again" on close (an opener that is not
     *  rendered any more). Absent: unchanged. */
    restoreFocus?: () => void
  } = {},
): void {
  useModalLayer(open, {
    root: () => {
      const panel = panelRef.current
      return panel ? ((panel.closest(MODAL_SELECTOR) as HTMLElement | null) ?? panel) : null
    },
    initialFocus: () => opts.initialFocus?.() ?? panelRef.current,
    restoreFocus: opts.restoreFocus,
    onKeyDown: (e: KeyboardEvent) => {
      const panel = panelRef.current
      if (e.key === 'Escape') {
        // An ActionMenu open above this modal owns the Escape: it closes the
        // menu, never the modal as well.
        if (isActionMenuOpen()) return
        onClose()
        return
      }
      if (e.key === 'Tab' && panel) {
        const focusables = panel.querySelectorAll<HTMLElement>(FOCUSABLE)
        if (focusables.length === 0) {
          e.preventDefault()
          panel.focus()
          return
        }
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        const active = document.activeElement
        if (e.shiftKey && (active === first || active === panel || !panel.contains(active))) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
          e.preventDefault()
          first.focus()
        }
      }
    },
  })
}

/** Test seam: how many layers are stacked (the shared stack). */
export function modalFocusDepth(): number {
  return modalDepth()
}
